import { QueryCache, QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { showsBillingActionBlockedModalAtom, store } from "@/utils/atoms";
import { isBillingActionBlockedError } from "@/utils/billingActionBlockedError";
import { shouldSuppressErrorNotification } from "@/utils/sessionExpiryError";

/**
 * `meta` for a query that shows its own error, so the global handler skips it
 * (as in React Query 4, where a query's own `onError` replaced the default).
 */
export const ownErrorHandlingMeta = { handlesOwnErrors: true };

export const useQueryClient = (
  handleError: (err: unknown, errorMessage?: string) => void,
) => {
  const queryClient = useMemo(() => {
    const onError = (err: unknown) => {
      if (isBillingActionBlockedError(err)) {
        store.set(showsBillingActionBlockedModalAtom, true);

        return;
      }

      if (!shouldSuppressErrorNotification(err)) {
        handleError(err);
      }
    };

    return new QueryClient({
      // Once per failed fetch - React Query 5 has no default `onError` for
      // queries any more.
      queryCache: new QueryCache({
        onError: (err, query) => {
          if (!query.meta?.handlesOwnErrors) {
            onError(err);
          }
        },
      }),
      defaultOptions: {
        queries: { retry: false },
        mutations: { onError },
      },
    });
  }, [handleError]);

  return queryClient;
};
